"use client";

import { useEffect, useRef, useState, useCallback } from "react";
import { Search, MapPin, Loader2 } from "lucide-react";
import styles from "./SearchBar.module.css";
import type { AddressData } from "@/types/address";
import { GOOGLE_MAPS_API_KEY } from "@/lib/constants";

// Google Maps API types are provided by @types/google.maps
// Note: As of March 1, 2025, both Autocomplete and AutocompleteService are deprecated for new customers.
// We use the new Places API (New) with importLibrary for custom UI implementations.

// Debounce helper function
function debounce<T extends (...args: unknown[]) => void | Promise<void>>(
	func: T,
	wait: number
): (...args: Parameters<T>) => void {
	let timeout: NodeJS.Timeout | null = null;
	return function executedFunction(...args: Parameters<T>) {
		const later = () => {
			timeout = null;
			func(...args);
		};
		if (timeout) clearTimeout(timeout);
		timeout = setTimeout(later, wait);
	};
}

interface SearchBarProps {
	onChange?: (value: string) => void;
	onAddressSelect?: (address: AddressData) => void;
	isLoading?: boolean; // Loading state from parent (when fetching properties)
	fullWidth?: boolean; // If true, search bar takes full width instead of 40%
	className?: string; // Additional className for container
	inputClassName?: string; // Additional className for input
	value?: string; // Controlled value prop
	/** Override default placeholder (e.g. street-address forms) */
	placeholder?: string;
	/** Smaller search icon for dense UIs */
	compactIcon?: boolean;
}

export default function SearchBar({
	onChange,
	onAddressSelect,
	isLoading = false,
	fullWidth = false,
	className,
	inputClassName,
	value,
	placeholder = "Search by State, County, City, and Zip code",
	compactIcon = false,
}: SearchBarProps) {
	const inputRef = useRef<HTMLInputElement>(null);
	const [inputValue, setInputValue] = useState(value || "");
	const [suggestions, setSuggestions] = useState<
		google.maps.places.AutocompletePrediction[]
	>([]);
	const [showSuggestions, setShowSuggestions] = useState(false);
	const [selectedIndex, setSelectedIndex] = useState(-1);
	const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
	const suggestionsRef = useRef<HTMLDivElement>(null);
	const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);
	const placesLibraryRef = useRef<Record<string, unknown> | null>(null);

	// Load Google Maps API using the new importLibrary approach
	useEffect(() => {
		if (typeof window === "undefined") return;

		if (!GOOGLE_MAPS_API_KEY) {
			console.warn(
				"Google Maps API key not found. Please add NEXT_PUBLIC_GOOGLE_MAPS_API_KEY to your .env file"
			);
			return;
		}

		// Helper to wait for importLibrary to be available
		const waitForImportLibrary = (
			maxAttempts = 50,
			delay = 100
		): Promise<void> => {
			return new Promise((resolve, reject) => {
				let attempts = 0;
				const check = () => {
					if (
						window.google?.maps &&
						typeof window.google.maps.importLibrary === "function"
					) {
						resolve();
					} else if (attempts < maxAttempts) {
						attempts++;
						setTimeout(check, delay);
					} else {
						reject(
							new Error(
								"importLibrary not available after waiting"
							)
						);
					}
				};
				check();
			});
		};

		// Load Google Maps API script if not already loaded
		const loadGoogleMaps = async () => {
			// Check if Google Maps is already loaded
			if (
				window.google?.maps &&
				typeof window.google.maps.importLibrary === "function"
			) {
				await initializePlacesLibrary();
				return;
			}

			// Check if script is already being loaded
			if (document.querySelector('script[src*="maps.googleapis.com"]')) {
				// Wait for it to load
				try {
					await waitForImportLibrary();
					await initializePlacesLibrary();
				} catch (error) {
					console.error(
						"Failed to wait for Google Maps to load:",
						error
					);
				}
				return;
			}

			// Load the script
			const script = document.createElement("script");
			script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&loading=async`;
			script.async = true;
			script.defer = true;
			script.onload = async () => {
				// Wait for importLibrary to be available after script loads
				try {
					await waitForImportLibrary();
					await initializePlacesLibrary();
				} catch (error) {
					console.error(
						"Failed to initialize after script load:",
						error
					);
				}
			};
			script.onerror = () => {
				console.error("Failed to load Google Maps API script");
			};
			document.head.appendChild(script);
		};

		const initializePlacesLibrary = async () => {
			try {
				// Double-check that importLibrary is available
				if (
					!window.google?.maps ||
					typeof window.google.maps.importLibrary !== "function"
				) {
					console.error("Google Maps importLibrary not available");
					return;
				}

				// Import the Places library using the new API
				const placesLibrary =
					await window.google.maps.importLibrary("places");

				// Log what's available for debugging
				console.log(
					"Places library loaded:",
					Object.keys(placesLibrary || {})
				);

				placesLibraryRef.current = placesLibrary as unknown as Record<
					string,
					unknown
				>;
				setIsGoogleMapsLoaded(true);
			} catch (error) {
				console.error("Failed to load Places library:", error);
			}
		};

		loadGoogleMaps();
	}, []);

	// Sync internal state when the controlled `value` prop changes from the parent only.
	// Omitting `inputValue` from deps avoids resetting the field while the user types before the parent updates.
	useEffect(() => {
		if (value !== undefined && value !== inputValue) {
			setInputValue(value);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- sync only on external `value` changes
	}, [value]);

	// Debounced function to update suggestions
	const debouncedUpdateSuggestionsRef = useRef<
		((value: string) => void) | null
	>(null);

	// Debounced function to call parent onChange (to prevent expensive re-renders)
	const debouncedOnChangeRef = useRef<((value: string) => void) | null>(null);

	useEffect(() => {
		debouncedUpdateSuggestionsRef.current = debounce(
			async (value: unknown) => {
				const searchValue = value as string;
				if (!isGoogleMapsLoaded || searchValue.length < 2) {
					setSuggestions([]);
					setShowSuggestions(false);
					setIsLoadingSuggestions(false);
					return;
				}

				setIsLoadingSuggestions(true);
				try {
					const request = {
						input: searchValue,
						types: ["address"],
						componentRestrictions: { country: "us" },
					};

					// Use AutocompleteService (fallback method that works reliably)
					if (window.google?.maps?.places?.AutocompleteService) {
						const service =
							new window.google.maps.places.AutocompleteService();
						service.getPlacePredictions(
							request,
							(
								predictionsOrResponse:
									| google.maps.places.AutocompletePrediction[]
									| null
									| {
											predictions?: unknown[];
											status?: string;
									  },
								status?: google.maps.places.PlacesServiceStatus
							) => {
								// Handle both (predictions, status) and ({ predictions, status }) callback shapes
								let list:
									| google.maps.places.AutocompletePrediction[]
									| null = null;
								let ok = false;
								if (
									Array.isArray(predictionsOrResponse) &&
									status ===
										google.maps.places.PlacesServiceStatus
											.OK
								) {
									list = predictionsOrResponse;
									ok = true;
								} else if (
									predictionsOrResponse &&
									typeof predictionsOrResponse === "object" &&
									"predictions" in predictionsOrResponse &&
									Array.isArray(
										(
											predictionsOrResponse as {
												predictions: unknown[];
											}
										).predictions
									)
								) {
									const res = predictionsOrResponse as {
										predictions: google.maps.places.AutocompletePrediction[];
										status: string;
									};
									if (res.status === "OK") {
										list = res.predictions;
										ok = true;
									}
								}
								if (ok && list && list.length > 0) {
									setSuggestions(list);
									setShowSuggestions(true);
									setSelectedIndex(-1);
								} else {
									setSuggestions([]);
									setShowSuggestions(false);
								}
								setIsLoadingSuggestions(false);
							}
						);
					} else {
						setSuggestions([]);
						setShowSuggestions(false);
						setIsLoadingSuggestions(false);
					}
				} catch (error) {
					console.error("Failed to get suggestions:", error);
					setSuggestions([]);
					setShowSuggestions(false);
					setIsLoadingSuggestions(false);
				}
			},
			300
		) as (value: string) => void;

		// Debounce parent onChange to prevent expensive re-renders on every keystroke
		debouncedOnChangeRef.current = debounce((value: unknown) => {
			const searchValue = value as string;
			onChange?.(searchValue);
		}, 150) as (value: string) => void;
	}, [isGoogleMapsLoaded, onChange]);

	// Initialize suggestions using new API (keeping for backward compatibility)
	useEffect(() => {
		if (
			!isGoogleMapsLoaded ||
			!inputRef.current ||
			!placesLibraryRef.current
		)
			return;

		const placesLibrary = placesLibraryRef.current;
		if (!placesLibrary) {
			console.warn("Places library not available");
			return;
		}

		const updateSuggestions = async () => {
			const inputValue = inputRef.current?.value || "";
			if (inputValue.length < 2) {
				setSuggestions([]);
				setShowSuggestions(false);
				setIsLoadingSuggestions(false);
				return;
			}

			setIsLoadingSuggestions(true);
			try {
				const request = {
					input: inputValue,
					types: ["address"],
					componentRestrictions: { country: "us" },
				};

				let response: {
					predictions?: google.maps.places.AutocompletePrediction[];
					suggestions?: Array<{
						placeId?: string;
						place_id?: string;
						text?: string;
						description?: string;
						structured_formatting?: {
							main_text?: string;
							secondary_text?: string;
						};
						placePrediction?: {
							text?: string;
						};
					}>;
				} | null = null;

				// Prefer AutocompleteService.getPlacePredictions (stable, still supported for existing keys).
				// Avoids AutocompleteSuggestion which has a different response structure and triggers console errors.
				if (
					typeof window !== "undefined" &&
					window.google?.maps?.places?.AutocompleteService
				) {
					const service =
						new window.google.maps.places.AutocompleteService();
					service.getPlacePredictions(
						request,
						(
							predictionsOrResponse:
								| google.maps.places.AutocompletePrediction[]
								| null
								| { predictions?: unknown[]; status?: string },
							status?: google.maps.places.PlacesServiceStatus
						) => {
							// Handle both (predictions, status) and ({ predictions, status }) callback shapes
							let list:
								| google.maps.places.AutocompletePrediction[]
								| null = null;
							let ok = false;
							if (
								Array.isArray(predictionsOrResponse) &&
								status ===
									google.maps.places.PlacesServiceStatus.OK
							) {
								list = predictionsOrResponse;
								ok = true;
							} else if (
								predictionsOrResponse &&
								typeof predictionsOrResponse === "object" &&
								"predictions" in predictionsOrResponse &&
								Array.isArray(
									(
										predictionsOrResponse as {
											predictions: unknown[];
										}
									).predictions
								)
							) {
								const res = predictionsOrResponse as {
									predictions: google.maps.places.AutocompletePrediction[];
									status: string;
								};
								if (res.status === "OK") {
									list = res.predictions;
									ok = true;
								}
							}
							if (ok && list && list.length > 0) {
								setSuggestions(list);
								setShowSuggestions(true);
								setSelectedIndex(-1);
							} else {
								setSuggestions([]);
								setShowSuggestions(false);
							}
						}
					);
					return; // Exit early since we're using callback
				}

				// Optional: try fetchSuggestions or AutocompleteSuggestion if AutocompleteService is not available
				if (
					placesLibrary.fetchSuggestions &&
					typeof placesLibrary.fetchSuggestions === "function"
				) {
					response = await placesLibrary.fetchSuggestions(request);
				} else if (
					placesLibrary.AutocompleteSuggestion &&
					typeof placesLibrary.AutocompleteSuggestion === "function"
				) {
					try {
						const AutocompleteSuggestionClass =
							placesLibrary.AutocompleteSuggestion as new (
								request: unknown
							) => Record<string, unknown>;
						const autocompleteSuggestion =
							new AutocompleteSuggestionClass(request);

						let foundMethod = false;
						for (const [, value] of Object.entries(
							autocompleteSuggestion
						)) {
							if (typeof value === "function") {
								try {
									const result = await (
										value as () => Promise<unknown>
									).call(autocompleteSuggestion);
									if (
										result &&
										typeof result === "object" &&
										("suggestions" in result ||
											Array.isArray(result))
									) {
										response = Array.isArray(result)
											? { suggestions: result }
											: (result as typeof response);
										foundMethod = true;
										break;
									}
								} catch {
									// Continue trying other methods
								}
							}
						}

						if (!foundMethod) {
							if (autocompleteSuggestion instanceof Promise) {
								response =
									(await autocompleteSuggestion) as typeof response;
							} else if (
								"suggestions" in autocompleteSuggestion
							) {
								const suggestions =
									autocompleteSuggestion.suggestions as Array<{
										placeId?: string;
										place_id?: string;
										text?: string;
										description?: string;
										structured_formatting?: {
											main_text?: string;
											secondary_text?: string;
										};
										placePrediction?: {
											text?: string;
										};
									}>;
								response = { suggestions };
							}
							// If structure not recognized, leave response null (no throw, no console noise)
						}
					} catch {
						// Silently fall through; response stays null
					}
				}

				// Handle both "suggestions" and "predictions" (Google API returns "predictions")
				const rawList =
					response?.predictions ?? response?.suggestions ?? [];
				if (rawList.length > 0) {
					// Normalize to AutocompletePrediction shape (predictions already have it; suggestions may need mapping)
					const predictions = (
						response?.predictions ? rawList : response!.suggestions!
					).map(
						(suggestion: {
							placeId?: string;
							place_id?: string;
							text?: string;
							description?: string;
							structured_formatting?: {
								main_text?: string;
								secondary_text?: string;
							};
							placePrediction?: {
								text?: string;
							};
						}) => {
							const placeId =
								suggestion.placeId || suggestion.place_id;
							const text =
								suggestion.text || suggestion.description || "";
							const mainText =
								suggestion.text ||
								suggestion.structured_formatting?.main_text ||
								text;
							const secondaryText =
								suggestion.placePrediction?.text ||
								suggestion.structured_formatting
									?.secondary_text ||
								"";

							return {
								place_id: placeId,
								description: text,
								structured_formatting: {
									main_text: mainText,
									secondary_text: secondaryText,
								},
							};
						}
					);
					setSuggestions(
						predictions as google.maps.places.AutocompletePrediction[]
					);
					setShowSuggestions(true);
					setSelectedIndex(-1);
				} else if (!response && placesLibrary) {
					// Only warn if we tried places library and got nothing
					console.warn(
						"No compatible autocomplete method found. Using AutocompleteService when available."
					);
					setSuggestions([]);
					setShowSuggestions(false);
				} else {
					setSuggestions([]);
					setShowSuggestions(false);
				}
				return;
			} catch (error) {
				console.error("Failed to get suggestions:", error);
				setSuggestions([]);
				setShowSuggestions(false);
			} finally {
				setIsLoadingSuggestions(false);
			}
		};

		// Trigger suggestions when input value changes
		if (inputRef.current) {
			updateSuggestions();
		}
	}, [isGoogleMapsLoaded, inputValue]);

	// Extract address data from Google Places result
	const extractAddressData = (
		place: google.maps.places.PlaceResult
	): AddressData => {
		const addressData: AddressData = {
			formattedAddress: place.formatted_address || "",
			placeId: place.place_id,
		};

		if (place.geometry?.location) {
			addressData.latitude = place.geometry.location.lat();
			addressData.longitude = place.geometry.location.lng();
		}

		if (place.address_components) {
			place.address_components.forEach(
				(component: google.maps.GeocoderAddressComponent) => {
					const types = component.types;

					if (types.includes("street_number")) {
						addressData.streetNumber = component.long_name;
					}
					if (types.includes("route")) {
						addressData.streetName = component.long_name;
					}
					if (types.includes("locality")) {
						addressData.city = component.long_name;
					}
					if (types.includes("administrative_area_level_2")) {
						addressData.county = component.long_name;
					}
					if (types.includes("administrative_area_level_1")) {
						addressData.state = component.long_name;
						addressData.stateCode = component.short_name;
					}
					if (types.includes("postal_code")) {
						addressData.zipCode = component.long_name;
					}
					if (types.includes("postal_code_suffix")) {
						addressData.zipCodeSuffix = component.long_name;
					}
					if (types.includes("neighborhood")) {
						addressData.neighborhood = component.long_name;
					}
					if (types.includes("country")) {
						addressData.country = component.long_name;
						addressData.countryCode = component.short_name;
					}
				}
			);
		}

		return addressData;
	};

	// Handle suggestion selection
	const handleSuggestionSelect = useCallback(
		async (prediction: google.maps.places.AutocompletePrediction) => {
			if (
				!isGoogleMapsLoaded ||
				!window.google?.maps?.places?.PlacesService
			) {
				return;
			}

			// Use PlacesService to get full place details
			let service: google.maps.places.PlacesService;
			try {
				service = new google.maps.places.PlacesService(
					document.createElement("div")
				);
			} catch (error) {
				console.error("Failed to initialize PlacesService:", error);
				return;
			}

			service.getDetails(
				{
					placeId: prediction.place_id,
					fields: [
						"address_components",
						"formatted_address",
						"geometry",
						"place_id",
					],
				},
				(
					place: google.maps.places.PlaceResult | null,
					status: google.maps.places.PlacesServiceStatus
				) => {
					if (
						status === google.maps.places.PlacesServiceStatus.OK &&
						place
					) {
						const addressData = extractAddressData(place);
						setInputValue(addressData.formattedAddress);
						setShowSuggestions(false);

						// Call onChange with formatted address
						if (onChange) {
							onChange(addressData.formattedAddress);
						}

						if (onAddressSelect) {
							onAddressSelect(addressData);
						}
					}
				}
			);
		},
		[isGoogleMapsLoaded, onChange, onAddressSelect]
	);

	// Handle keyboard navigation
	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (showSuggestions && suggestions.length > 0) {
			switch (e.key) {
				case "ArrowDown":
					e.preventDefault();
					setSelectedIndex((prev) =>
						prev < suggestions.length - 1 ? prev + 1 : prev
					);
					return;
				case "ArrowUp":
					e.preventDefault();
					setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
					return;
				case "Enter":
					e.preventDefault();
					if (selectedIndex >= 0 && suggestions[selectedIndex]) {
						handleSuggestionSelect(suggestions[selectedIndex]);
					}
					return;
				case "Escape":
					setShowSuggestions(false);
					setSelectedIndex(-1);
					return;
			}
		}

		// Handle Enter key when no suggestions are shown or no suggestion is selected
		if (e.key === "Enter" && (!showSuggestions || selectedIndex === -1)) {
			e.preventDefault();
			const currentValue = inputRef.current?.value || "";
			if (currentValue.trim() && onChange) {
				onChange(currentValue.trim());
			}
			setShowSuggestions(false);
		}
	};

	// Close suggestions when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				suggestionsRef.current &&
				!suggestionsRef.current.contains(event.target as Node) &&
				inputRef.current &&
				!inputRef.current.contains(event.target as Node)
			) {
				setShowSuggestions(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	// Show loading when fetching suggestions or when parent is loading (fetching properties)
	const showLoading = isLoadingSuggestions || isLoading;

	return (
		<div
			className={`${styles.searchContainer} ${fullWidth ? styles.fullWidth : ""} ${className || ""}`}
		>
			{showLoading ? (
				<Loader2
					className={`${styles.searchIcon} ${compactIcon ? styles.searchIconSmall : ""} ${styles.loadingIcon}`}
					style={{ left: compactIcon ? "10px" : "16px" }}
				/>
			) : (
				<Search
					style={{ left: compactIcon ? "10px" : "16px" }}
					className={`${styles.searchIcon} ${compactIcon ? styles.searchIconSmall : ""}`}
				/>
			)}
			<input
				ref={inputRef}
				type="text"
				placeholder={placeholder}
				value={inputValue}
				data-testid="search-bar-input"
				onChange={(e) => {
					const newValue = e.target.value;
					// Update input value immediately for responsive typing
					setInputValue(newValue);
					// Debounce parent onChange to prevent expensive re-renders
					if (debouncedOnChangeRef.current) {
						debouncedOnChangeRef.current(newValue);
					}
					// Debounce suggestions update
					if (debouncedUpdateSuggestionsRef.current) {
						debouncedUpdateSuggestionsRef.current(newValue);
					}
				}}
				onKeyDown={handleKeyDown}
				onFocus={() => {
					if (suggestions.length > 0) {
						setShowSuggestions(true);
					}
				}}
				onBlur={() => {
					// Delay hiding to allow clicks on suggestions
					setTimeout(() => {
						setShowSuggestions(false);
					}, 200);
				}}
				className={`${styles.searchInput} ${inputClassName || ""}`}
				disabled={isLoading}
			/>
			{showSuggestions && suggestions.length > 0 && (
				<div
					ref={suggestionsRef}
					className={styles.suggestionsDropdown}
					data-testid="autocomplete-suggestions-container"
				>
					{suggestions.map((suggestion, index) => (
						<button
							key={suggestion.place_id ?? `suggestion-${index}`}
							type="button"
							onMouseDown={(e) => e.preventDefault()}
							onClick={() => handleSuggestionSelect(suggestion)}
							className={`${styles.suggestionItem} ${
								index === selectedIndex
									? styles.suggestionItemActive
									: ""
							}`}
							data-testid="autocomplete-suggestion-item"
						>
							<MapPin className={styles.suggestionIcon} />
							<div className={styles.suggestionContent}>
								<div className={styles.suggestionMainText}>
									{suggestion.structured_formatting
										?.main_text ??
										suggestion.description ??
										""}
								</div>
								<div className={styles.suggestionSecondaryText}>
									{suggestion.structured_formatting
										?.secondary_text ?? ""}
								</div>
							</div>
						</button>
					))}
				</div>
			)}
		</div>
	);
}
